import Decimal from 'decimal.js'
import { DateTime } from 'luxon'

import { type IProcessedBudgetData } from '@webapp/components/Budgets/SpendTracker'
import { type TimezonesEnum } from '@webapp/core/entities/external/enums/TimezonesEnum'
import { type BudgetFormat } from '@webapp/hooks/useFormatBudget'
import {
  type AccountCode,
  type BudgetCategory,
  type Department,
  type User,
  type BudgetGroupBudget,
} from '@webapp/models'
import { type AllocationsAggregate } from '@webapp/models/AllocationsAggregate'
import { type BudgetIntervalType } from '@webapp/models/enums/BudgetInterval'
import { getFullName } from '@webapp/utils/user'

export interface BudgetBreakdownBar {
  label: string
  progressValue: number
  color: string
  striped?: boolean
}

export interface DepartmentalSpendCategoryCardData {
  approvedSpend: number
  invoicedSpend: number
  purchasedSpend: number
  currentSpend: number
  otherPendingRequests: number
  spendAfterApproval: number
  currentRequestSpend: number
  oldPendingSpend: number
  // remove above with FT_5817_ENABLE_BUDGETS_REDESIGN removal
  committedSpend: number
  requestedSpend: number
  totalSpendBeforePending: number
  pendingSpend: number
  totalSpend: number
  startDate: Date
  endDate: Date
}

export interface IBudgetCategoryCardData {
  name: string
  uuid: uuid
  startDate: Date
  endDate: Date
  budget: Nullable<string>
  budgetBreakdown: BudgetBreakdownBar[]
  currentSpend: string
  currentRequestSpend: string
  period: Omit<BudgetIntervalType, 'none'> | null
  variance: Nullable<string>
  variancePercentage: Nullable<string>
  pendingSpend: string
  otherPendingRequests: string
  spendAfterApproval: string
  attributes: {
    departments: string[]
    accountCodes: string[]
  }
  isOverBudget: boolean
  isSourceOfTruth?: boolean
}

export const getCurrentYear = () => new Date().getFullYear()

/**
 * This function returns width values used to display the current spend, current request spend and other pending request spend values as a percentage relative to the current spend.
 * If the current spend or the cumulative sum of current spend, current request spend and other pending request spend values does not exceed 100, then the width values returned will
 * be represented as a percentage of 100 as expected.
 * i.e. if the budget is 2000, current spend is 1000, current request is 300 and other pending requests is 500, then the respective values returned are 50, 15, and 25
 *
 * The values will not be representative if the total sum of current spend, current request spend and other pending request spend exceeds 100. i.e.
 * if the budget is 2000, current spend is 1600, current request is 300 and other pending requests is 500, then the respective values returned are 80, 15, and 5
 * if the budget is 2000, current spend is 1900, current request is 300 and other pending requests is 500, then the respective values returned are 95, and 5
 *
 * In other words, the values for current request spend and other pending request spend will always be the remainder of the available width remaining from the current spend value.
 * Visual examples can be found in Storybook and in PR #3148
 *
 * @deprecated replaced by useGetBudgetBreakdownhook - can be removed when all domains have been migrated to Flexible Budgets i.e. Budget Categories vs Spend Categories
 * @param currentSpend The total spend (approved, purchased, invoiced) that match the relevant Spend Category i.e. the sum of approved, purchased and invoiced spend totalling 1600
 * @param currentRequestSpend The spend associated with the current request i.e. an RFO requesting to spend 300
 * @param otherPendingRequestSpend The spend associated with other pending spend that matches the relevant Spend Category attributes i.e. a separate RFO requesting to spend 500
 * @param budget The total budget allotment for the relevant Spend Category i.e. a buget amount of 2000
 * @returns {Object} An object containing the multi progress bar width values
 */
export const getBudgetProgressValuesRelativeToCurrentSpend = (
  currentSpend: number,
  currentRequestSpend: number,
  otherPendingRequestSpend: number,
  budget: number
): {
  currentSpendProgressValue: number
  currentRequestSpendProgressValue: number
  otherPendingRequestSpendProgressValue?: number
} => {
  let remainingBudgetWidth = 100

  const currentSpendProgressValue = (currentSpend / budget) * 100

  remainingBudgetWidth -= currentSpendProgressValue

  let currentRequestSpendProgressValue = (currentRequestSpend / budget) * 100

  if (currentRequestSpendProgressValue > remainingBudgetWidth) {
    currentRequestSpendProgressValue = remainingBudgetWidth

    return {
      currentSpendProgressValue,
      currentRequestSpendProgressValue,
    }
  }

  remainingBudgetWidth = Math.max(
    remainingBudgetWidth - currentRequestSpendProgressValue,
    0
  )

  let otherPendingRequestSpendProgressValue =
    (otherPendingRequestSpend / budget) * 100

  if (otherPendingRequestSpendProgressValue > remainingBudgetWidth) {
    otherPendingRequestSpendProgressValue = remainingBudgetWidth
  }

  return {
    currentSpendProgressValue,
    currentRequestSpendProgressValue,
    otherPendingRequestSpendProgressValue,
  }
}

/**
 * @deprecated replaced by useGetBudgetBreakdownhook - can be removed when all domains have been migrated to Flexible Budgets i.e. Budget Categories vs Spend Categories
 */
export const getBudgetBreakdown = (
  budgetedSpend: number,
  currentSpend: number,
  currentRequestSpend: number,
  spendAfterApproval: number,
  otherPendingRequests: number
): BudgetBreakdownBar[] => {
  const isOverBudget = spendAfterApproval >= budgetedSpend

  if (!isOverBudget) {
    const {
      currentSpendProgressValue,
      currentRequestSpendProgressValue,
      otherPendingRequestSpendProgressValue,
    } = getBudgetProgressValuesRelativeToCurrentSpend(
      currentSpend,
      currentRequestSpend,
      otherPendingRequests,
      budgetedSpend
    )

    return [
      {
        label: 'Current spend bar',
        progressValue: currentSpendProgressValue,
        color: 'darkGreen',
      },
      {
        label: 'Current request bar',
        progressValue: currentRequestSpendProgressValue,
        color: 'darkGreen',
        striped: true,
      },
      {
        label: 'Other pending requests bar',
        progressValue: otherPendingRequestSpendProgressValue,
        color: 'yellow',
      },
    ]
  } else {
    const currentSpendProgressValue = (currentSpend / budgetedSpend) * 100
    const remainingBudget = 100 - currentSpendProgressValue

    return [
      {
        label: 'Current spend bar',
        progressValue:
          currentSpendProgressValue > 100 ? 100 : currentSpendProgressValue,
        color: 'red',
      },
      {
        label: 'Current request bar',
        progressValue: remainingBudget > 0 ? remainingBudget : 0,
        color: 'red',
        striped: true,
      },
    ]
  }
}

export const parseCategoryVarianceValues = (
  variance: string,
  variancePercentage: string
): { variance: number; variancePercentage: number } => {
  return {
    variance: !!variance ? new Decimal(variance).toNumber() : null,
    variancePercentage: !!variancePercentage
      ? new Decimal(variancePercentage).toNumber()
      : null,
  }
}

export const getSpendTrackerSpendValues = (
  approved: string = '0',
  invoiced: string = '0',
  pending: string = '0',
  purchased: string = '0',
  requestPendingAmount: string = '0',
  received: string = '0'
): {
  approvedSpend: number
  invoicedSpend: number
  pendingSpend: number
  purchasedSpend: number
  currentSpend: number
  otherPendingRequests: number
  spendAfterApproval: number
  currentRequestSpend: number
  receivedSpend: number
} => {
  const zero = new Decimal(0)
  const approvedSpend = new Decimal(approved)
  const invoicedSpend = new Decimal(invoiced)
  const pendingSpend = new Decimal(pending)
  const purchasedSpend = new Decimal(purchased)
  const currentRequestSpend = new Decimal(requestPendingAmount)
  const receivedSpend = new Decimal(received)

  const currentSpend = purchasedSpend.add(approvedSpend).add(invoicedSpend)

  const pendingSpendLessCurrentRequestSpend =
    pendingSpend.minus(currentRequestSpend)

  const otherPendingRequests = pendingSpendLessCurrentRequestSpend.lessThan(
    zero
  )
    ? zero
    : pendingSpendLessCurrentRequestSpend

  const spendAfterApproval = currentSpend.add(currentRequestSpend)

  return {
    approvedSpend: approvedSpend.toNumber(),
    invoicedSpend: invoicedSpend.toNumber(),
    pendingSpend: pendingSpend.toNumber(),
    purchasedSpend: purchasedSpend.toNumber(),
    currentSpend: currentSpend.toNumber(),
    otherPendingRequests: otherPendingRequests.toNumber(),
    spendAfterApproval: spendAfterApproval.toNumber(),
    currentRequestSpend: currentRequestSpend.toNumber(),
    receivedSpend: receivedSpend.toNumber(),
  }
}

export const formatDepartmentalSpendCardData = (
  {
    approved,
    invoiced,
    pending,
    purchased,
    received,
    request_pending_amount,
  }: AllocationsAggregate = {
    approved: '0',
    invoiced: '0',
    pending: '0',
    purchased: '0',
    received: '0',
    request_pending_amount: '0',
  },
  timeZone: TimezonesEnum
): DepartmentalSpendCategoryCardData => {
  const startDate = getDateObject(
    `${getCurrentYear()}-01-01T00:00:00`,
    timeZone
  )
  const endDate = getDateObject(`${getCurrentYear()}-12-31T23:59:59`, timeZone)

  const {
    approvedSpend,
    invoicedSpend,
    pendingSpend,
    purchasedSpend,
    currentSpend,
    otherPendingRequests,
    spendAfterApproval,
    currentRequestSpend,
    receivedSpend,
  } = getSpendTrackerSpendValues(
    approved,
    invoiced,
    pending,
    purchased,
    request_pending_amount,
    received
  )

  const committedSpend =
    approvedSpend + purchasedSpend + invoicedSpend + receivedSpend
  const totalSpendBeforePending = committedSpend + currentRequestSpend
  const totalSpend = totalSpendBeforePending + otherPendingRequests

  return {
    approvedSpend,
    invoicedSpend,
    purchasedSpend,
    currentSpend,
    otherPendingRequests,
    spendAfterApproval,
    currentRequestSpend,
    oldPendingSpend: pendingSpend,
    // remove above with FT_5817_ENABLE_BUDGETS_REDESIGN removal
    committedSpend,
    requestedSpend: currentRequestSpend,
    totalSpendBeforePending,
    pendingSpend: otherPendingRequests,
    totalSpend,

    startDate,
    endDate,
  }
}

/**
 * @deprecated getDateObject should be removed when Flexible Budgets is enabled for all customers, this is replaced by useFormatISOStringToDate
 */
export const getDateObject = (
  isoDateTime: string,
  timeZone: TimezonesEnum
): Date => {
  if (!isoDateTime || !timeZone) return

  const { day, month, year, hour, minute, second } = DateTime.fromISO(
    isoDateTime,
    { zone: timeZone }
  ).toObject()

  return new Date(year, month - 1, day, hour, minute, second)
}

/**
 * @deprecated convertISOStringToDate should be removed when Flexible Budgets is enabled for all customers, this is replaced by useFormatISOStringToDate
 */
export const convertISOStringToDate = (
  isoString: string,
  timeZone: TimezonesEnum,
  isStartDate: boolean = false,
  autoUseCurrentYear: boolean = true
) => {
  if (isStartDate) {
    return isoString
      ? getDateObject(isoString, timeZone)
      : autoUseCurrentYear
        ? getDateObject(`${getCurrentYear()}-01-01T00:00:00`, timeZone)
        : null
  }

  return isoString
    ? getDateObject(isoString, timeZone)
    : autoUseCurrentYear
      ? getDateObject(`${getCurrentYear()}-12-31T23:59:59`, timeZone)
      : null
}

// To be used exclusively for formatting data for CategoryCard components
// currently supports:
// - SpendTrackerDashboardCard (dashboard view)
// will be updated to support:
// - SpendTrackerCategoryCard (approvals view)
// - SpendCategoryListCard (settings view)
export const formatBudgetCategoryCardData = (
  {
    name,
    uuid,
    start_datetime,
    end_datetime,
    approved,
    invoiced,
    pending,
    purchased,
    request_pending_amount = '0',
    attributes,
    budget,
    interval,
    variance,
    variance_percentage,
    source_of_truth,
  }: BudgetCategory,
  timeZone: TimezonesEnum,
  autoUseCurrentYear = true
): IBudgetCategoryCardData => {
  const startDate = convertISOStringToDate(
    start_datetime,
    timeZone,
    true,
    autoUseCurrentYear
  )
  const endDate = convertISOStringToDate(
    end_datetime,
    timeZone,
    false,
    autoUseCurrentYear
  )

  const {
    pendingSpend,
    currentSpend,
    otherPendingRequests,
    spendAfterApproval,
    currentRequestSpend,
  } = getSpendTrackerSpendValues(
    approved,
    invoiced,
    pending,
    purchased,
    request_pending_amount
  )

  const budgetedSpend = budget ? new Decimal(budget).toNumber() : null

  const budgetBreakdown = budgetedSpend
    ? getBudgetBreakdown(
        budgetedSpend,
        currentSpend,
        currentRequestSpend,
        spendAfterApproval,
        otherPendingRequests
      )
    : null

  const { variance: _variance } = parseCategoryVarianceValues(
    variance,
    variance_percentage
  )

  const isOverBudget = _variance < 0

  const { departments = [], account_codes = [] } = attributes

  return {
    name,
    uuid,
    startDate,
    endDate,
    budget,
    budgetBreakdown,
    currentSpend: currentSpend.toString(),
    currentRequestSpend: currentRequestSpend.toString(),
    pendingSpend: pendingSpend.toString(),
    period: interval,
    spendAfterApproval: spendAfterApproval.toString(),
    otherPendingRequests: otherPendingRequests.toString(),
    variance,
    variancePercentage: variance_percentage,
    attributes: {
      departments: formatSecondaryOrgCategoryAttribute(departments),
      accountCodes: formatAccountCodeAttribute(account_codes),
    },
    isOverBudget,
    isSourceOfTruth: source_of_truth,
  }
}

export const calculateBudgetTrackerCardData = (
  budgets: BudgetCategory[],
  headquartersTimezone: TimezonesEnum,
  calculateBudgetSpendDetails: (budget: BudgetGroupBudget) => BudgetFormat,
  formatISOStringToDate: (
    isoDateTimeString: string,
    timezone: TimezonesEnum,
    useHeadQuarterTimezone?: boolean
  ) => Date
): IProcessedBudgetData[] => {
  return budgets.map((budget) => {
    const budgetSpendDetails = calculateBudgetSpendDetails({
      id: budget.id,
      amount: budget.budget,
      approved: budget.approved,
      pending: budget.pending,
      purchased: budget.purchased,
      invoiced: budget.invoiced,
      received: budget.received,
      request_pending_amount: budget.request_pending_amount,
      start_datetime: budget?.start_datetime,
      end_datetime: budget?.end_datetime,
      variance: budget.variance,
      variance_percentage: budget.variance_percentage,
    })

    const startDate = budget?.start_datetime
      ? formatISOStringToDate(budget.start_datetime, headquartersTimezone)
      : null

    const endDate = budget?.end_datetime
      ? formatISOStringToDate(budget.end_datetime, headquartersTimezone)
      : null

    return {
      startDate,
      endDate,
      budgetSpendDetails,
      name: budget.name,
      uuid: budget.uuid,
      period: budget.interval,
      isSourceOfTruth: budget.source_of_truth,
      attributes: {
        departments: formatSecondaryOrgCategoryAttribute(
          budget.attributes.departments
        ),
        accountCodes: formatAccountCodeAttribute(
          budget.attributes.account_codes
        ),
      },
    }
  })
}

export const formatSpendStringToNumber = (spend: string = '0.00'): number =>
  new Decimal(spend).toNumber()

export const formatSecondaryOrgCategoryAttribute = (
  secondaryOrgCategories: Department[]
) =>
  secondaryOrgCategories?.length &&
  secondaryOrgCategories.map(
    (secondaryOrgCategory) =>
      `${secondaryOrgCategory?.branch?.name} - ${secondaryOrgCategory?.name}`
  )

export const formatAccountCodeAttribute = (accountCodes: AccountCode[]) =>
  accountCodes?.length &&
  accountCodes.map(
    (accountCode) => `${accountCode.code} - ${accountCode.description}`
  )

export const formatViewersAttribute = (viewers: User[]) =>
  viewers?.length && viewers.map((viewer) => getFullName(viewer))
